// The empties pane's own toolbar (Alan, 2026-09-27: "分屏后是完全的两套一样的操作
// 系统 ... 右边是空柜左边是一个可以自由筛选的池").
//
// The split's left pane carries the Dashboard's real toolbar - the same buttons, moved
// into it while the split is up (see adoptToolbarIntoPane in app.js). This is its twin
// for the right pane: the same classes, so it looks the same, and the same controls,
// less the ones a pane that only ever holds reported empties cannot use - the Loaded /
// Empty / All chips and 可提 would each empty it. Everything else is here: 已派单, the
// three missing-field chips, a date range, search, Copy Container #, 批量上传 and
// Filters.
//
// It narrows the right pane only. Every control writes into one filter object the
// caller owns and then asks the caller to re-ask its question; the caller turns that
// into a server query (emptiesPaneQuery in app.js), so the server's rules decide what
// matches, exactly as they do for the toolbar.

import { mountDashboardFilterPopover } from './dashboard-filter-popover.mjs?v=20260806.100';
import { mountDashboardCopyMenu } from './dashboard-copy-menu.mjs?v=20260806.100';
import { createDashboardBulkCopyController } from './dashboard-bulk-copy.mjs?v=20260806.100';


export const PANE_MISSING_CHIPS = Object.freeze([
  ['eta', 'eta_at', 'No ETA', '无ETA'],
  ['ssl', 'ssl', 'No SSL', '无船司'],
  ['terminal', 'terminal_name', 'No Terminal', '无码头'],
]);

function escapeHtml(value) {
  return String(value ?? '').replaceAll('&', '&amp;').replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#039;');
}

const isZh = (locale) => String(locale).toLowerCase().startsWith('zh');

/** A filter that narrows nothing. The date field is kept: choosing one narrows nothing. */
export function blankPaneFilter(dateField = 'actual_empty_returned_at') {
  return {
    search: '', from: '', to: '', ssl: '', terminal_name: '', dateField,
    dispatched: false, missing: [],
  };
}

/**
 * A chip is a fresh start, as it is on the toolbar (Alan, 2026-08-13: "点击就等于说
 * 直接只显示这些并清楚其他的filter"): 已派单 is a view of its own, and the missing
 * chips accumulate among themselves and release everything else.
 */
export function pressPaneChip(filter, chip) {
  const blank = blankPaneFilter(filter?.dateField || undefined);
  if (chip === 'dispatched') return { ...blank, dispatched: !filter?.dispatched };
  const current = Array.isArray(filter?.missing) ? filter.missing : [];
  const missing = current.includes(chip) ? current.filter((kind) => kind !== chip) : [...current, chip];
  return { ...blank, missing };
}

/**
 * Blank, absent, whitespace, or a date at or before 1970 - "nobody has filled it in".
 *
 * The server's dashboardFieldIsMissing, for the rows as the browser holds them (a date
 * is a Date here and millis there). The two cannot share code, so both suites assert
 * against tests/fixtures/missing-field-cases.json.
 */
export function rowFieldIsMissing(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (value instanceof Date) return !(value.getTime() > 0);
  if (typeof value === 'number') return !Number.isFinite(value) || value <= 0;
  return false;
}

/**
 * What the chips count over the rows given: released to a driver, and each field still
 * blank. The server counts the fleet the same way (summarizeCargoPools).
 */
export function paneChipCounts(rows) {
  const counts = { dispatched: 0, missing: { eta: 0, ssl: 0, terminal: 0 } };
  for (const row of Array.isArray(rows) ? rows : []) {
    if (!row || typeof row !== 'object') continue;
    if (row.dispatch_released === true) counts.dispatched += 1;
    for (const [kind, field] of PANE_MISSING_CHIPS) {
      if (rowFieldIsMissing(row[field])) counts.missing[kind] += 1;
    }
  }
  return counts;
}

export function paneToolbarMarkup({ iconFor = () => '', role = '', locale = 'zh' } = {}) {
  const zh = isZh(locale);
  const missing = PANE_MISSING_CHIPS.map(([kind, , en, cn]) => (
    `<button type="button" class="dashboard-cargo-chip dashboard-cargo-chip--missing" data-pane-missing="${kind}" aria-pressed="false" hidden><span class="dashboard-cargo-chip__label">${escapeHtml(zh ? cn : en)}</span><span class="dashboard-cargo-chip__count" data-pane-missing-count="${kind}" aria-hidden="true"></span></button>`
  )).join('');
  return `<div class="dashboard-work-pool-bar" data-pane-work-pool-bar>
      <div class="dashboard-cargo-filter dashboard-cargo-filter--missing" role="group" aria-label="${zh ? '缺少的信息' : 'Missing information'}">${missing}</div>
    </div>
    <label class="topbar-date topbar-date--range"><span>From</span><input type="date" data-pane-from aria-label="${zh ? '空柜日期起' : 'Empties range start'}"></label>
    <label class="topbar-date topbar-date--range"><span>To</span><input type="date" data-pane-to aria-label="${zh ? '空柜日期止' : 'Empties range end'}"></label>
    <label class="topbar-search"><span class="topbar-control-icon">${iconFor('search')}</span><input type="search" data-pane-search placeholder="${zh ? '搜索空柜...' : 'Search empties...'}" aria-label="${zh ? '搜索空柜' : 'Search empties'}" autocomplete="off" spellcheck="false"></label>
    <button class="topbar-compact-action topbar-copy-containers" type="button" data-pane-copy aria-label="${zh ? '复制空柜柜号' : 'Copy the empties\' Container numbers'}" aria-live="polite"><span class="topbar-control-icon" data-copy-icon aria-hidden="true">${iconFor('copy')}</span><span data-copy-label>Copy Container #</span></button>
    <button class="topbar-compact-action" type="button" data-pane-filter aria-haspopup="dialog" aria-expanded="false">Filters</button>`;
}

const COPY_LABELS = Object.freeze({
  idle: () => 'Copy Container #',
  copying: (count) => (count > 0 ? `Copying ${count}…` : 'Copying…'),
  success: (count) => `Copied ${count}`,
  empty: () => 'No containers',
  limit: () => 'Refine filters',
  error: () => 'Retry copy',
});

/**
 * Binds the twin toolbar drawn by paneToolbarMarkup into `toolbar`.
 *
 * `filter` is the caller's object and is written in place; `onFilterChange` re-asks
 * the pane's question. `table()` is the pane's table controller, whose drawn order is
 * what Copy and 批量上传 work in. `counts()` answers paneChipCounts over every empty,
 * or null while that is not known - the chips then show no number rather than a
 * wrong one.
 */
export function mountPaneToolbar({
  toolbar,
  filter,
  onFilterChange,
  counts = () => null,
  table = () => null,
  optionRows = () => [],
  overlayManager,
  columns = [],
  role = '',
  locale = () => 'zh',
  clipboard = null,
  save = null,
  iconFor = () => '',
  setTimeoutRef = globalThis.setTimeout?.bind(globalThis),
  clearTimeoutRef = globalThis.clearTimeout?.bind(globalThis),
}) {
  if (!toolbar || !filter || typeof onFilterChange !== 'function'
    || typeof overlayManager?.open !== 'function') {
    throw new TypeError('Pane toolbar options are invalid.');
  }
  const activeLocale = () => (typeof locale === 'function' ? locale() : locale);
  toolbar.innerHTML = paneToolbarMarkup({ iconFor, role, locale: activeLocale() });
  const pick = (selector) => toolbar.querySelector(selector);
  const search = pick('[data-pane-search]');
  const from = pick('[data-pane-from]');
  const to = pick('[data-pane-to]');
  const dispatchedChip = pick('[data-pane-dispatched]');
  const missingChips = [...toolbar.querySelectorAll('[data-pane-missing]')];
  const copyButton = pick('[data-pane-copy]');
  const filterButton = pick('[data-pane-filter]');
  const cleanups = [];
  const listen = (element, type, handler) => {
    if (!element) return;
    element.addEventListener(type, handler);
    cleanups.push(() => element.removeEventListener(type, handler));
  };

  // The boxes are written only when the filter changed under them - on mount, and
  // after a chip or Filters rewrote it. Writing them on every repaint would wipe a
  // search being typed whenever a row landed.
  function paintInputs() {
    if (search) search.value = filter.search ?? '';
    if (from) from.value = filter.from ?? '';
    if (to) to.value = filter.to ?? '';
  }

  // The chips, safe at any moment: their state and their numbers.
  function paint() {
    dispatchedChip?.setAttribute('aria-pressed', String(filter.dispatched === true));
    const known = counts();
    const dispatchedCount = dispatchedChip?.querySelector('[data-pane-dispatched-count]');
    if (dispatchedCount) {
      const count = known?.dispatched;
      dispatchedCount.textContent = Number.isSafeInteger(count) ? String(count) : '';
      dispatchedCount.hidden = !Number.isSafeInteger(count);
    }
    for (const chip of missingChips) {
      const kind = chip.dataset.paneMissing;
      const pressed = (filter.missing ?? []).includes(kind);
      chip.setAttribute('aria-pressed', String(pressed));
      const count = known?.missing?.[kind];
      const target = chip.querySelector('[data-pane-missing-count]');
      if (target) target.textContent = Number.isSafeInteger(count) && count > 0 ? String(count) : '';
      // As on the toolbar: hidden at zero, never while pressed, and - with no count
      // known yet - shown, so the control is there to use.
      chip.hidden = Number.isSafeInteger(count) && count === 0 && !pressed;
    }
    const filtersOn = Boolean(filter.ssl || filter.terminal_name);
    filterButton?.setAttribute('aria-pressed', String(filtersOn));
  }

  const commit = ({ inputs = true } = {}) => {
    if (inputs) paintInputs();
    paint();
    onFilterChange();
  };

  let typing = null;
  listen(search, 'input', () => {
    if (typing !== null) clearTimeoutRef?.(typing);
    typing = setTimeoutRef?.(() => {
      typing = null;
      filter.search = search.value;
      onFilterChange();
    }, 220);
  });
  const onRange = () => {
    filter.from = from?.value || '';
    filter.to = to?.value || '';
    commit({ inputs: false });
  };
  listen(from, 'change', onRange);
  listen(to, 'change', onRange);
  const pressChip = (chip) => {
    Object.assign(filter, pressPaneChip(filter, chip));
    commit();
  };
  listen(dispatchedChip, 'click', () => pressChip('dispatched'));
  for (const chip of missingChips) listen(chip, 'click', () => pressChip(chip.dataset.paneMissing));

  const popover = filterButton ? mountDashboardFilterPopover({
    trigger: filterButton,
    overlayManager,
    // Every row here is reported empty, so status boxes could only empty the pane.
    columns: (Array.isArray(columns) ? columns : []).filter(({ key }) => key !== 'ocean_status'),
    initial: filter,
    rows: optionRows,
    onApply(next) {
      filter.ssl = String(next.ssl || '');
      filter.terminal_name = String(next.terminal_name || '');
      filter.dateField = String(next.dateField || 'actual_empty_returned_at');
      filter.from = String(next.from || '');
      filter.to = String(next.to || '');
      commit();
    },
  }) : null;
  if (popover) cleanups.push(() => popover.destroy());

  // Copy works on this pane's rows in the order it draws them.
  let copyResetTimer = null;
  const paneNumbers = () => (table()?.rowsInDisplayOrder?.() ?? [])
    .map((row) => String(row?.container_no ?? '').trim());
  const renderCopyState = ({ status, count = 0 }) => {
    if (!copyButton?.isConnected) return;
    if (copyResetTimer !== null) {
      clearTimeoutRef?.(copyResetTimer);
      copyResetTimer = null;
    }
    const label = copyButton.querySelector('[data-copy-label]');
    if (label) label.textContent = (COPY_LABELS[status] ?? COPY_LABELS.idle)(count);
    const icon = copyButton.querySelector('[data-copy-icon]');
    if (icon) icon.innerHTML = iconFor(status === 'success' ? 'check' : 'copy');
    copyButton.disabled = status === 'copying';
    copyButton.dataset.copyState = status;
    if (['success', 'empty', 'limit', 'error'].includes(status)) {
      copyResetTimer = setTimeoutRef?.(() => copyController?.cancel(), 1800);
    }
  };
  const copyController = copyButton && clipboard ? createDashboardBulkCopyController({
    clipboard, onState: renderCopyState, numbers: paneNumbers, maxItems: 5000,
  }) : null;
  const afterCopy = (result) => {
    if (result?.copied) {
      table()?.markCopiedContainers?.(result.numbers);
    }
    return result;
  };
  const copyMenu = copyController ? mountDashboardCopyMenu({
    trigger: copyButton,
    overlayManager,
    maxItems: 5000,
    locale: activeLocale,
    total: () => copyController.available(),
    onCopy: (size, index) => copyController.copyGroup(null, { size, index }).then(afterCopy).catch(() => {}),
    onCopyAll: () => copyController.copy(null).then(afterCopy).catch(() => {}),
    onPreviewGroup: (size, index) => table()?.previewCopyGroup?.(copyController.groupNumbers({ size, index })),
    markedCount: () => table()?.copiedContainerCount?.() || 0,
    onClearMarks: () => table()?.clearCopiedContainers?.(),
  }) : null;
  if (copyMenu) cleanups.push(() => copyMenu.destroy());

  paintInputs();
  paint();
  return Object.freeze({
    paint,
    destroy() {
      if (typing !== null) clearTimeoutRef?.(typing);
      if (copyResetTimer !== null) clearTimeoutRef?.(copyResetTimer);
      copyController?.cancel();
      for (const cleanup of cleanups.splice(0)) cleanup();
    },
  });
}
